import { type Row, type Table } from "@tanstack/react-table";
import { useCallback } from "react";

import {
	AddIcon,
	ArrowDownIcon,
	ArrowUpIcon,
	ArrowsVerticalIcon,
	DownToBottomIcon,
	OverflowMenuVerticalIcon,
	ReplicateIcon,
	TrashIcon,
	UpToTopIcon,
} from "@moment/icons";
import { DropdownMenu, MomentButton } from "@moment/ui";

import { emptyRow } from "../useColumnState";

export interface RowControlsMenuProps<T> {
	table: Table<T>;
	row: Row<T>;
}

const disabledMessage = (
	<>
		{"To allow mutation, provde an "}
		<code>onDataValueChange</code>
		{" callback to the table"}
	</>
);

export function RowControlsMenu<T>({ table, row }: RowControlsMenuProps<T>) {
	const canMutateData = table.options.meta?.tableEditable;

	const insertRow = useCallback(
		(rowIndex: number, rowData?: T) => {
			const newRow = rowData ?? (emptyRow(table as Table<unknown>) as T);
			table.options.meta?.insertRow?.(rowIndex, newRow);
		},
		[table.options.meta?.insertRow]
	);
	const moveRowToIndex = table.options.meta?.moveRowToIndex;
	const deleteRow = table.options.meta?.deleteRow;

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<MomentButton
					className="!h-[43px] !w-6 !rounded-none !rounded-l !p-0"
					icon={OverflowMenuVerticalIcon}
					label=""
					size="sm"
					level="tertiary"
				/>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content align="end">
					{/* <DropdownMenu.Item>
						<Maximize />
						<div>Expand row</div>
					</DropdownMenu.Item>
					<DropdownMenu.Separator /> */}
					<DropdownMenu.Sub>
						<DropdownMenu.SubTrigger
							asChild={false}
							disabledMessage={disabledMessage}
							disabled={!canMutateData}
						>
							<AddIcon />
							<div>Insert row</div>
						</DropdownMenu.SubTrigger>
						<DropdownMenu.Portal>
							<DropdownMenu.SubContent>
								<DropdownMenu.Item onSelect={() => insertRow?.(row.index)}>
									<ArrowUpIcon />
									<div>Insert row above</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item onSelect={() => insertRow?.(row.index + 1)}>
									<ArrowDownIcon />
									<div>Insert row below</div>
								</DropdownMenu.Item>
							</DropdownMenu.SubContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Sub>
					<DropdownMenu.Sub>
						<DropdownMenu.SubTrigger
							asChild={false}
							disabledMessage={disabledMessage}
							disabled={!canMutateData}
						>
							<ArrowsVerticalIcon />
							<div>Move row</div>
						</DropdownMenu.SubTrigger>
						<DropdownMenu.Portal>
							<DropdownMenu.SubContent>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, row.index - 1)}
								>
									<ArrowUpIcon />
									<div>Move row up</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, row.index + 1)}
								>
									<ArrowDownIcon />
									<div>Move row down</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item onSelect={() => moveRowToIndex?.(row.index, 0)}>
									<UpToTopIcon />
									<div>Move row to top</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, "end")}
								>
									<DownToBottomIcon />
									<div>Move row to bottom</div>
								</DropdownMenu.Item>
							</DropdownMenu.SubContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Sub>
					<DropdownMenu.Item
						onSelect={() => insertRow?.(row.index + 1, row.original)}
						disabledMessage={disabledMessage}
						disabled={!canMutateData}
					>
						<ReplicateIcon />
						<div>Duplicate row below</div>
					</DropdownMenu.Item>

					<DropdownMenu.Separator />
					<DropdownMenu.Item
						variant="negative"
						onSelect={() => deleteRow?.(row.index)}
						disabledMessage={disabledMessage}
						disabled={!canMutateData}
					>
						<TrashIcon />
						<div>Delete row</div>
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}
